// Minimal GTM utilities to initialize Tag Manager and push events safely

export const isGtmId = (id?: string): boolean => {
  if (!id) return false;
  return /^GTM-[A-Z0-9]+$/i.test(id);
};

type DataLayerEvent = Record<string, unknown>;

interface WindowWithGtm extends Window {
  dataLayer?: DataLayerEvent[] & { push: (e: DataLayerEvent) => number };
  __gtmInitialized?: boolean;
  __gtmId?: string;
  __trackingContext?: {
    visitor_id?: string;
    user_id?: string;
    campaign_id?: string;
    campaign_name?: string;
  };
}

export const initializeGtm = (gtmId: string): void => {
  if (typeof window === 'undefined') return;
  if (!isGtmId(gtmId)) return;

  // Avoid duplicate initialization
  const win = window as unknown as WindowWithGtm;
  if (win.__gtmInitialized) return;

  // Ensure dataLayer exists and push gtm.js event
  win.dataLayer =
    win.dataLayer || ([] as unknown as WindowWithGtm['dataLayer']);
  const dataLayer = win.dataLayer as NonNullable<WindowWithGtm['dataLayer']>;
  dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  });

  const firstScript = document.getElementsByTagName('script')[0];
  const gtmScript = document.createElement('script');
  gtmScript.async = true;
  gtmScript.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  firstScript?.parentNode?.insertBefore(gtmScript, firstScript);

  // Optional noscript iframe for no-JS environments
  // Insert once right after <body>
  const noscript = document.createElement('noscript');
  const iframe = document.createElement('iframe');
  iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
  iframe.height = '0';
  iframe.width = '0';
  iframe.style.display = 'none';
  iframe.style.visibility = 'hidden';
  noscript.appendChild(iframe);
  document.body?.insertBefore(noscript, document.body.firstChild);

  win.__gtmInitialized = true;
  win.__gtmId = gtmId;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const pushToDataLayer = (
  eventName: string,
  payload?: Record<string, unknown>,
): void => {
  try {
    const win = window as unknown as WindowWithGtm;
    if (!win || !win.dataLayer || typeof win.dataLayer.push !== 'function')
      return;
    const context = win.__trackingContext || {};
    const eventObject: DataLayerEvent = {
      event: eventName,
      ...context,
      ...(payload || {}),
    };
    win.dataLayer.push(eventObject);
  } catch {
    // no-op
  }
};

export const getOrCreateVisitorId = (): string => {
  try {
    const key = 'wg_visitor_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const id = crypto?.randomUUID
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(key, id);
    return id;
  } catch {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
};

export const setTrackingContext = (ctx: {
  visitor_id?: string;
  user_id?: string;
  campaign_id?: string;
  campaign_name?: string;
}): void => {
  try {
    const win = window as unknown as WindowWithGtm;
    win.__trackingContext = {
      ...(win.__trackingContext || {}),
      ...ctx,
    };
  } catch {
    // no-op
  }
};
